import { useCallback, useMemo, useState } from "react";
import { type Corrections, type GoesTo, goesTo, type Room, type Turn } from "../composer/addressed.ts";
import type { Candidate } from "../composer/mentions.ts";
import type { Line } from "../person/arrivals.ts";

const UNDER = "rally-routing:";

function store(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function autoRoutingIn(conversation: string): boolean {
  try {
    return store()?.getItem(UNDER + conversation) !== "off";
  } catch {
    return true;
  }
}

export function keepAutoRouting(conversation: string, on: boolean): void {
  try {
    if (on) store()?.removeItem(UNDER + conversation);
    else store()?.setItem(UNDER + conversation, "off");
  } catch {}
}

const AS_FAR_BACK_AS = 8;

export function roomOf(input: {
  me: string;
  text: string;
  names: readonly Candidate[];
  lines: readonly Line[];

  replyTo?: string | null;
}): Room {
  const byAccount = new Map(input.names.map((one) => [one.account, one]));
  const handleOf = (account: string): string => byAccount.get(account)?.handle ?? account;
  const history: Turn[] = input.lines
    .filter((one) => one.kind === "message" && one.by && one.text)
    .slice(-AS_FAR_BACK_AS)
    .map((one) => ({
      handle: handleOf(one.by ?? ""),
      isAgent: byAccount.get(one.by ?? "")?.kind === "agent",
      text: one.text ?? "",
    }));
  return {
    sender: handleOf(input.me),
    text: input.text,
    people: input.names.filter((one) => one.kind === "person").map((one) => one.handle),
    agents: input.names.filter((one) => one.kind === "agent").map((one) => one.handle),
    history,
    replyTo: input.replyTo
      ? {
          handle: handleOf(input.replyTo),
          isAgent: byAccount.get(input.replyTo)?.kind === "agent",
        }
      : null,
  };
}

export type Routing = {
  said: GoesTo[];

  likely: string[];

  anything: boolean;
  on: boolean;
  setOn: (on: boolean) => void;

  drop: (handle: string) => void;

  add: (handle: string) => void;

  forget: () => void;
};

export function routingIsVisible(text: string, on: boolean, said: readonly GoesTo[]): boolean {
  return text.trim() !== "" && (!on || said.some((who) => who.word !== "unlikely"));
}

export function useRouting(conversation: string, room: Room): Routing {
  const [held, setHeld] = useState<{ for: string; corrections: Corrections }>({
    for: conversation,
    corrections: {},
  });
  const [switched, setSwitched] = useState<{ for: string; on: boolean }>({
    for: conversation,
    on: autoRoutingIn(conversation),
  });

  if (held.for !== conversation) setHeld({ for: conversation, corrections: {} });
  if (switched.for !== conversation) {
    setSwitched({ for: conversation, on: autoRoutingIn(conversation) });
  }
  const on = switched.for === conversation ? switched.on : autoRoutingIn(conversation);
  const corrections = held.for === conversation ? held.corrections : {};

  const setOn = useCallback(
    (next: boolean) => {
      keepAutoRouting(conversation, next);
      setSwitched({ for: conversation, on: next });
    },
    [conversation],
  );
  const drop = useCallback(
    (handle: string) => {
      setHeld((was) => ({
        for: conversation,
        corrections: {
          dropped: [...(was.for === conversation ? (was.corrections.dropped ?? []) : []), handle],
          added: (was.for === conversation ? (was.corrections.added ?? []) : []).filter(
            (one) => one !== handle,
          ),
        },
      }));
    },
    [conversation],
  );
  const add = useCallback(
    (handle: string) => {
      setHeld((was) => ({
        for: conversation,
        corrections: {
          dropped: (was.for === conversation ? (was.corrections.dropped ?? []) : []).filter(
            (one) => one !== handle,
          ),
          added: [...(was.for === conversation ? (was.corrections.added ?? []) : []), handle],
        },
      }));
    },
    [conversation],
  );
  const forget = useCallback(() => setHeld({ for: conversation, corrections: {} }), [conversation]);

  const alreadyRouted = room.people.length === 1 && room.agents.length === 1;

  const said = useMemo(
    () => (room.text.trim() === "" ? [] : goesTo(room, on && !alreadyRouted, corrections)),
    [room, on, alreadyRouted, corrections],
  );
  const likely = said.filter((who) => who.word === "likely").map((who) => who.handle);

  const anything = routingIsVisible(room.text, on, said);
  return { said, likely, anything, on, setOn, drop, add, forget };
}
